/** Shopify collection sort values that are directional pairs. */
export const SORT_PAIR_BY_VALUE = {
  'title-ascending': 'title-descending',
  'title-descending': 'title-ascending',
  'price-ascending': 'price-descending',
  'price-descending': 'price-ascending',
  'created-ascending': 'created-descending',
  'created-descending': 'created-ascending',
  'sku-ascending': 'sku-descending',
  'sku-descending': 'sku-ascending',
};

/** Canonical (ascending) value for each invertible family. */
export const SORT_FAMILY_CANONICAL = {
  title: 'title-ascending',
  price: 'price-ascending',
  created: 'created-ascending',
  sku: 'sku-ascending',
};

const HIDDEN_SORTS = new Set(['manual']);
const CLIENT_SORTS = new Set(['sku-ascending', 'sku-descending']);

export function isSortHidden(value) {
  return HIDDEN_SORTS.has(String(value || ''));
}

export function isClientSort(value) {
  return CLIENT_SORTS.has(String(value || ''));
}

export function readSortByFromHref(href) {
  try {
    return new URL(href).searchParams.get('sort_by') || '';
  } catch {
    return '';
  }
}

function cardSku(card) {
  if (!card) return '';
  const fromDataset = card.dataset?.sku;
  if (fromDataset != null && String(fromDataset).trim()) return String(fromDataset).trim();
  if (typeof card.getAttribute === 'function') {
    return String(card.getAttribute('data-sku') || '').trim();
  }
  return '';
}

/** Reorder specimen cards in the collection grid by data-sku. Empty SKUs sort last. */
export function sortProductGridBySku(root, direction = 'ascending') {
  const container = root?.querySelector?.('[data-collection-product-grid]');
  const grid =
    container?.querySelector?.('.grid.grid--4') || container?.querySelector?.('.grid');
  if (!grid?.querySelectorAll || typeof grid.appendChild !== 'function') return;

  const cards = Array.from(grid.querySelectorAll('.specimen-card'));
  if (cards.length < 2) return;

  const sign = direction === 'descending' ? -1 : 1;
  cards.sort((a, b) => {
    const sa = cardSku(a);
    const sb = cardSku(b);
    if (!sa && !sb) return 0;
    if (!sa) return 1;
    if (!sb) return -1;
    return sign * sa.localeCompare(sb, 'es', { sensitivity: 'base', numeric: true });
  });

  for (const card of cards) grid.appendChild(card);
}

export function applyClientCollectionSort(root, href) {
  const sortBy = readSortByFromHref(href);
  if (!isClientSort(sortBy)) return;
  sortProductGridBySku(root, isSortDescending(sortBy) ? 'descending' : 'ascending');
}

export function getSortPair(value) {
  return SORT_PAIR_BY_VALUE[String(value || '')] || null;
}

export function isSortInvertible(value) {
  return getSortPair(value) != null;
}

export function isSortDescending(value) {
  return String(value || '').endsWith('-descending');
}

/**
 * Family key used as the <option> value in the collapsed select.
 * Invertible sorts collapse to their ascending form; others keep their value.
 */
export function getSortFamilyValue(value) {
  const sort = String(value || '');
  if (!sort || isSortHidden(sort)) return null;
  if (sort.endsWith('-descending')) {
    const ascending = getSortPair(sort);
    return ascending || sort;
  }
  return sort;
}

/**
 * Resolve the real Shopify sort_by from a family option + invert state.
 */
export function resolveSortBy(familyValue, inverted) {
  const family = String(familyValue || '');
  if (!family || isSortHidden(family)) return null;
  if (!inverted) return family;
  const pair = getSortPair(family);
  return pair || family;
}

/**
 * Collapse Shopify sort_options into unique families (skip manual; one per A/Z pair).
 * @param {Array<{ value: string, name?: string }>} options
 * @returns {Array<{ value: string, name: string, invertible: boolean }>}
 */
export function collapseSortOptions(options) {
  if (!Array.isArray(options)) return [];
  const seen = new Set();
  const collapsed = [];

  for (const option of options) {
    const raw = option?.value;
    if (raw == null || isSortHidden(raw)) continue;
    const family = getSortFamilyValue(raw);
    if (!family || seen.has(family)) continue;
    seen.add(family);
    collapsed.push({
      value: family,
      name: String(option.name || family),
      invertible: isSortInvertible(family),
    });
  }

  return collapsed;
}

export function buildSortUrl(href, sortBy) {
  const url = new URL(href);
  const value = String(sortBy || '').trim();
  if (!value || isSortHidden(value)) {
    url.searchParams.delete('sort_by');
  } else {
    url.searchParams.set('sort_by', value);
  }
  url.searchParams.delete('page');
  return url.toString();
}

function navigateToSort(sortBy, assignLocation, currentHref) {
  const href =
    currentHref ||
    (typeof window !== 'undefined' && window.location ? window.location.href : '');
  if (!href || typeof assignLocation !== 'function') return;
  assignLocation(buildSortUrl(href, sortBy));
}

export function syncSortInvertControl(select, button) {
  if (!select || !button) return;
  const family = select.value;
  const invertible = isSortInvertible(family);
  button.disabled = !invertible;
  button.setAttribute('aria-disabled', invertible ? 'false' : 'true');
  const inverted = button.getAttribute('aria-pressed') === 'true';
  if (!invertible && inverted) {
    button.setAttribute('aria-pressed', 'false');
  }
}

export function bindCollectionSortControls({
  select,
  invertButton,
  currentSort,
  assignLocation,
  currentHref,
} = {}) {
  if (!select) return;

  const go = (sortBy) => {
    const navigate =
      assignLocation ||
      ((url) => {
        if (typeof window !== 'undefined' && window.location) {
          window.location.href = url;
        }
      });
    navigateToSort(sortBy, navigate, currentHref);
  };

  const initialFamily = getSortFamilyValue(currentSort) || select.value;
  if (initialFamily) {
    const option = Array.from(select.options).find((o) => o.value === initialFamily);
    if (option) select.value = initialFamily;
  }

  if (invertButton) {
    const inverted = isSortDescending(currentSort);
    invertButton.setAttribute('aria-pressed', inverted ? 'true' : 'false');
    syncSortInvertControl(select, invertButton);

    invertButton.addEventListener('click', () => {
      if (invertButton.disabled) return;
      const nextInverted = invertButton.getAttribute('aria-pressed') !== 'true';
      const sortBy = resolveSortBy(select.value, nextInverted);
      if (!sortBy) return;
      go(sortBy);
    });
  }

  select.addEventListener('change', () => {
    const inverted =
      invertButton && isSortInvertible(select.value)
        ? invertButton.getAttribute('aria-pressed') === 'true'
        : false;
    const sortBy = resolveSortBy(select.value, inverted);
    if (!sortBy) return;
    if (invertButton) syncSortInvertControl(select, invertButton);
    go(sortBy);
  });
}

export function initCollectionSort(root = document, assignLocation) {
  const select = root.querySelector?.('[data-collection-sort]');
  if (!select) return;
  const invertButton = root.querySelector?.('[data-collection-sort-invert]');
  const href =
    typeof window !== 'undefined' && window.location ? window.location.href : '';
  const currentSort =
    readSortByFromHref(href) ||
    select.dataset.currentSort ||
    select.value;
  bindCollectionSortControls({
    select,
    invertButton,
    currentSort,
    assignLocation,
  });
  applyClientCollectionSort(root, href);
}

if (typeof window !== 'undefined' && !globalThis.__ASTRO_COLLECTION_SORT_INIT__) {
  globalThis.__ASTRO_COLLECTION_SORT_INIT__ = true;

  const initialize = () => {
    if (document.querySelector('[data-collection-sort]')) {
      initCollectionSort();
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }
}
